import React, { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ShieldCheck, Zap, CheckCircle2, ArrowRight, XCircle, Sparkles, Search, Eye, SkipForward, Star, Download, ExternalLink, ChevronDown, ChevronUp } from "lucide-react";
import api, { apiErrorMessage, getToken, API_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export function Billing() {
  const { user } = useAuth();
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/payments/me").then((r) => setSub(r.data)).catch(() => {});
  }, []);

  const goToCheckout = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: "driver_pro_monthly",
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(apiErrorMessage(err));
      setLoading(false);
    }
  };

  const perks = [
    "Unlimited bids on any open request",
    "Featured placement in the driver directory",
    "Priority admin support & fast KYC review",
    "Auto-highlight your bid with a Driver Pro badge",
  ];

  return (
    <div className="px-6 md:px-10 py-10 md:py-14 max-w-3xl mx-auto" data-testid="billing-page">
      <div className="mb-10">
        <span className="text-xs uppercase tracking-[0.3em] text-[#FF3B30] font-bold">/ Billing</span>
        <h1 className="font-display text-4xl md:text-5xl font-black uppercase mt-2 leading-none">
          Driver Pro
        </h1>
        <p className="text-sm text-zinc-500 mt-3">
          You get 3 free bids. Go Pro for unlimited bidding + featured placement.
        </p>
      </div>

      {sub?.active ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="border border-emerald-500/40 bg-emerald-500/[0.06] p-6"
          data-testid="sub-active-panel"
        >
          <div className="flex items-center gap-3 mb-3">
            <CheckCircle2 className="w-5 h-5 text-emerald-400" />
            <h2 className="font-display text-2xl font-black uppercase">You're on Driver Pro</h2>
          </div>
          <p className="text-sm text-zinc-400">Unlimited bids unlocked. Roll out and win jobs.</p>
        </motion.div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="border border-white/[0.08] bg-[#0F0F10] p-8 relative overflow-hidden"
        >
          <div className="absolute -right-10 -top-10 w-40 h-40 bg-[#FF3B30]/20 blur-[80px] pointer-events-none" />
          <div className="relative">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#FF3B30] font-bold mb-2">
              <Sparkles className="w-3.5 h-3.5" /> Pro
            </div>
            <div className="flex items-baseline gap-2 mb-6">
              <span className="font-display text-6xl font-black uppercase">$15</span>
              <span className="text-zinc-500 text-sm font-semibold">/ month</span>
            </div>
            <ul className="space-y-3 mb-8">
              {perks.map((p) => (
                <li key={p} className="flex items-start gap-3 text-sm text-zinc-300">
                  <div className="mt-0.5 w-4 h-4 bg-[#FF3B30] flex items-center justify-center shrink-0">
                    <CheckCircle2 className="w-3 h-3 text-black" strokeWidth={3} />
                  </div>
                  {p}
                </li>
              ))}
            </ul>
            <button
              onClick={goToCheckout}
              disabled={loading || user?.role !== "driver"}
              data-testid="checkout-btn"
              className="group w-full bg-[#FF3B30] hover:bg-[#D62828] disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold uppercase tracking-widest border-2 border-[#FF3B30] hover:border-[#D62828] py-4 px-6 flex items-center justify-center gap-3 transition-all"
            >
              {loading ? "Redirecting…" : user?.role !== "driver" ? "Drivers only" : "Upgrade to Pro"}
              <ArrowRight className={`w-4 h-4 transition-transform ${loading ? "" : "group-hover:translate-x-1"}`} />
            </button>
            <p className="text-[10px] uppercase tracking-widest text-zinc-600 mt-4 text-center">
              Test mode · Card 4242 4242 4242 4242 · Any future date · Any CVC
            </p>
          </div>
        </motion.div>
      )}
    </div>
  );
}

export function BillingSuccess() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState({ payment_status: "pending" });
  const [tries, setTries] = useState(0);

  useEffect(() => {
    if (!sessionId) return;
    let t;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setStatus(data);
        if (data.payment_status === "paid") return;
      } catch {
        /* ignore transient */
      }
      setTries((n) => n + 1);
    };
    poll();
    t = setInterval(() => {
      if (status.payment_status !== "paid" && tries < 20) poll();
    }, 2000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, tries, status.payment_status]);

  const paid = status.payment_status === "paid";

  return (
    <div className="px-6 md:px-10 py-14 max-w-md mx-auto text-center" data-testid="billing-success">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className={`inline-flex w-16 h-16 items-center justify-center mb-6 ${paid ? "bg-emerald-500" : "bg-[#FF3B30]"}`}
      >
        {paid ? <CheckCircle2 className="w-8 h-8 text-black" strokeWidth={2.5} /> : <Zap className="w-8 h-8 text-black animate-pulse" strokeWidth={2.5} />}
      </motion.div>
      <h1 className="font-display text-4xl font-black uppercase leading-tight">
        {paid ? "You're on Driver Pro." : "Confirming your payment…"}
      </h1>
      <p className="text-sm text-zinc-500 mt-3">
        {paid ? "Unlimited bids unlocked. Roll out." : "This takes a few seconds."}
      </p>
      <button
        onClick={() => nav("/app/driver")}
        data-testid="success-continue"
        className="mt-8 inline-flex items-center gap-2 bg-[#FF3B30] hover:bg-[#D62828] text-white font-bold uppercase tracking-widest text-xs border-2 border-[#FF3B30] hover:border-[#D62828] py-3 px-6 transition-all"
      >
        Browse jobs <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
}

export function BillingCancel() {
  return (
    <div className="px-6 md:px-10 py-14 max-w-md mx-auto text-center" data-testid="billing-cancel">
      <div className="inline-flex w-16 h-16 items-center justify-center mb-6 bg-zinc-800">
        <XCircle className="w-8 h-8 text-white" strokeWidth={2.5} />
      </div>
      <h1 className="font-display text-4xl font-black uppercase leading-tight">Cancelled</h1>
      <p className="text-sm text-zinc-500 mt-3">No charges made. Come back when you're ready.</p>
      <Link
        to="/app/billing"
        className="mt-8 inline-flex items-center gap-2 border-2 border-white/20 hover:border-white text-white font-bold uppercase tracking-widest text-xs py-3 px-6 transition-all"
      >
        Back to billing <ArrowRight className="w-4 h-4" />
      </Link>
    </div>
  );
}

// -------- KYC upload page --------
export function KycPage() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const isShipper = user?.role === "shipper";

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/kyc/me");
      setStatus(data);
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const DOC_META = {
    license: { label: "Driver's license", note: "Front photo, all four corners visible" },
    insurance: { label: "Insurance certificate", note: "Current commercial auto or trailer policy" },
    dot: { label: "DOT certificate", note: "Active USDOT number document" },
    photo_id: { label: "Photo ID", note: "Government-issued ID, all four corners visible" },
    ownership_proof: { label: "Proof of ownership", note: "Title, registration, or bill of sale for the rig you're shipping" },
  };
  const docTypes = (status?.required || []).map((k) => ({ key: k, ...DOC_META[k] }));
  const n = docTypes.length;

  return (
    <div className="px-6 md:px-10 py-10 md:py-14 max-w-3xl mx-auto" data-testid="kyc-page">
      <div className="mb-8">
        <span className="text-xs uppercase tracking-[0.3em] text-[#FF3B30] font-bold">/ Verification</span>
        <h1 className="font-display text-4xl md:text-5xl font-black uppercase mt-2 leading-none">KYC</h1>
        <p className="text-sm text-zinc-500 mt-3">
          {isShipper
            ? "Upload both docs to earn the Verified shipper badge — drivers see it before they bid."
            : "Upload all three docs to unlock the Verified badge. Reviewed manually within 24 hours."}
        </p>
      </div>
      {status?.verified && (
        <div className="mb-6 flex items-center gap-3 bg-emerald-500/10 border border-emerald-500/30 p-4" data-testid="verified-banner">
          <ShieldCheck className="w-5 h-5 text-emerald-400" />
          <div>
            <div className="font-bold text-sm uppercase tracking-widest">
              Verified {isShipper ? "shipper" : "driver"}
            </div>
            <div className="text-xs text-zinc-400">
              {isShipper ? "Your requests display the verified badge to drivers." : "Your public profile displays the verified badge."}
            </div>
          </div>
        </div>
      )}
      <div className="space-y-4">
        {docTypes.map((d) => (
          <KycTile
            key={d.key}
            docType={d.key}
            label={d.label}
            note={d.note}
            current={status?.docs?.[d.key]}
            onChange={load}
            loading={loading}
          />
        ))}
      </div>
    </div>
  );
}

function KycTile({ docType, label, note, current, onChange, loading }) {
  const [uploading, setUploading] = useState(false);
  const inputId = `file-${docType}`;

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File exceeds 5 MB");
      return;
    }
    setUploading(true);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      await api.post("/kyc", { doc_type: docType, filename: file.name, data_url: dataUrl });
      toast.success(`${label} submitted for review`);
      onChange();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const statusBadge = (s) => {
    if (!s) return <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Not uploaded</span>;
    const map = {
      submitted: { c: "bg-amber-500/90 text-black", t: "In review" },
      approved: { c: "bg-emerald-500/90 text-black", t: "Approved" },
      rejected: { c: "bg-red-500/90 text-white", t: "Rejected" },
    };
    const m = map[s.status] || map.submitted;
    return <span className={`text-[10px] uppercase tracking-widest font-black px-2 py-1 ${m.c}`}>{m.t}</span>;
  };

  return (
    <div className="border border-white/[0.08] bg-[#0F0F10] p-5" data-testid={`kyc-tile-${docType}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-display text-lg font-black uppercase tracking-tight">{label}</div>
          <div className="text-xs text-zinc-500 mt-1">{note}</div>
        </div>
        {statusBadge(current)}
      </div>
      {current?.admin_note && (
        <div className="text-xs text-red-400 mt-3 bg-red-500/[0.06] border border-red-500/20 px-3 py-2">
          {current.admin_note}
        </div>
      )}
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <input
          id={inputId}
          type="file"
          accept="image/png,image/jpeg,image/webp,application/pdf"
          className="hidden"
          onChange={onPick}
          disabled={loading || uploading}
          data-testid={`kyc-input-${docType}`}
        />
        <label
          htmlFor={inputId}
          className={`inline-flex items-center gap-2 text-xs uppercase tracking-widest font-bold cursor-pointer border-2 py-2.5 px-4 transition-colors ${
            uploading ? "border-white/10 text-zinc-500" : "border-[#FF3B30] text-white hover:bg-[#FF3B30]"
          }`}
          data-testid={`kyc-upload-${docType}`}
        >
          {uploading ? "Uploading…" : current ? "Replace" : "Upload"}
        </label>
        {current && (
          <span className="text-[11px] text-zinc-500 truncate">
            {current.filename} · {(current.size_bytes / 1024).toFixed(0)} KB
          </span>
        )}
      </div>
    </div>
  );
}

// -------- Admin KYC review console --------
const REJECTION_PRESETS = [
  "Too blurry / unreadable",
  "Document expired",
  "Wrong document type",
  "Name doesn't match account",
];

export function AdminKyc() {
  const { user } = useAuth();
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("pending");
  const [search, setSearch] = useState("");
  const [review, setReview] = useState(null); // { item, doc, file, note }
  const [expanded, setExpanded] = useState(null);
  const [previews, setPreviews] = useState({}); // docId -> { data_url, mime } | "error" | undefined (loading)
  const [inlineNotes, setInlineNotes] = useState({}); // docId -> rejection reason draft
  const [inlineBusy, setInlineBusy] = useState(null); // docId with an in-flight inline review
  const [focusIdx, setFocusIdx] = useState(0); // focused doc within the expanded row (keyboard review)

  const rawUrl = (docId, dl = false) =>
    `${API_BASE}/admin/kyc/${docId}/file/raw?token=${encodeURIComponent(getToken())}${dl ? "&download=1" : ""}`;

  const load = async () => {
    try {
      const { data } = await api.get("/admin/kyc/queue");
      setQueue(data);
      return data;
    } catch (err) {
      toast.error(apiErrorMessage(err));
      return null;
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const openReview = async (item, doc) => {
    try {
      const { data } = await api.get(`/admin/kyc/${doc.id}/file`);
      setReview({ item, doc, file: data, note: "" });
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  };

  const toggleExpand = async (item) => {
    if (expanded === item.user_id) {
      setExpanded(null);
      return;
    }
    setExpanded(item.user_id);
    setFocusIdx(Math.max(0, item.docs.findIndex((d) => d.status === "submitted")));
    // lazily pull side-by-side previews for this driver's docs
    const missing = item.docs.filter((d) => !(d.id in previews));
    if (missing.length === 0) return;
    const results = await Promise.all(
      missing.map(async (d) => {
        try {
          const { data } = await api.get(`/admin/kyc/${d.id}/file`);
          return [d.id, data];
        } catch {
          return [d.id, "error"];
        }
      })
    );
    setPreviews((p) => ({ ...p, ...Object.fromEntries(results) }));
  };

  const submitInline = async (doc, approve) => {
    const note = (inlineNotes[doc.id] || "").trim();
    if (!approve && !note) {
      toast.error("Add a short reason — the driver sees it.");
      return;
    }
    if (inlineBusy) return;
    setInlineBusy(doc.id);
    try {
      await api.post(`/admin/kyc/${doc.id}/review`, { approve, note });
      toast.success(approve ? `${doc.doc_type} approved` : `${doc.doc_type} rejected`);
      await load();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setInlineBusy(null);
    }
  };

  const submitReview = async (approve) => {
    if (!review) return;
    try {
      await api.post(`/admin/kyc/${review.doc.id}/review`, { approve, note: review.note });
      toast.success(approve ? `${review.doc.doc_type} approved` : `${review.doc.doc_type} rejected`);
      // auto-advance to this driver's next pending doc
      const fresh = await load();
      const freshItem = fresh?.find((x) => x.user_id === review.item.user_id);
      const next = freshItem?.docs.find((d) => d.status === "submitted");
      if (next) await openReview(freshItem, next);
      else setReview(null);
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  };

  const skipToNext = async () => {
    if (!review) return;
    const next = review.item.docs.find((d) => d.status === "submitted" && d.id !== review.doc.id);
    if (next) await openReview(review.item, next);
    else setReview(null);
  };

  // Keyboard review: ←/→ switch the focused doc, A approve, R reject (R focuses
  // the reason box first if empty). Modal open: A/R act on the modal, Esc closes,
  // → jumps to the next pending doc. Never fires while typing in a field.
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select" || e.target.isContentEditable) return;

      if (review) {
        if (e.key === "Escape") { setReview(null); return; }
        if (e.key === "ArrowRight") { e.preventDefault(); skipToNext(); return; }
        if (review.doc.status !== "submitted") return;
        if (e.key === "a" || e.key === "A") { e.preventDefault(); submitReview(true); }
        if (e.key === "r" || e.key === "R") {
          e.preventDefault();
          if (!review.note.trim()) document.querySelector('[data-testid="review-note"]')?.focus();
          else submitReview(false);
        }
        return;
      }

      const item = queue.find((x) => x.user_id === expanded);
      if (!item || !item.docs.length) return;
      const docs = item.docs;
      if (e.key === "ArrowRight" || e.key === "ArrowDown") {
        e.preventDefault();
        setFocusIdx((i) => Math.min(i + 1, docs.length - 1));
      } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
        e.preventDefault();
        setFocusIdx((i) => Math.max(i - 1, 0));
      } else if (e.key === "a" || e.key === "A") {
        const d = docs[focusIdx];
        if (d && d.status === "submitted") { e.preventDefault(); submitInline(d, true); }
      } else if (e.key === "r" || e.key === "R") {
        const d = docs[focusIdx];
        if (!d || d.status !== "submitted") return;
        e.preventDefault();
        if (!(inlineNotes[d.id] || "").trim()) {
          document.querySelector(`[data-testid="inline-note-${d.id}"]`)?.focus();
        } else {
          submitInline(d, false);
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  if (user && user.role !== "admin") {
    return <div className="p-14 text-red-400 uppercase text-sm">Admin only.</div>;
  }

  const q = search.trim().toLowerCase();
  const filtered = queue.filter((item) => {
    if (tab === "pending" && item.pending_count === 0) return false;
    if (q && !`${item.name} ${item.email}`.toLowerCase().includes(q)) return false;
    return true;
  });
  const totalPending = queue.reduce((n, i) => n + i.pending_count, 0);

  return (
    <div className="px-6 md:px-10 py-10 md:py-14 max-w-5xl mx-auto" data-testid="admin-kyc-page">
      <div className="mb-8">
        <span className="text-xs uppercase tracking-[0.3em] text-[#FF3B30] font-bold">/ Admin</span>
        <h1 className="font-display text-4xl md:text-5xl font-black uppercase mt-2 leading-none">
          KYC review
        </h1>
        <p className="text-sm text-zinc-500 mt-3">
          {totalPending > 0
            ? `${totalPending} document${totalPending === 1 ? "" : "s"} waiting. All three must be approved for a driver to show as Verified.`
            : "All caught up. New submissions land here."}
        </p>
      </div>

      <div className="flex items-center gap-3 flex-wrap mb-6">
        <div className="flex border border-white/[0.08]" data-testid="kyc-tabs">
          {[
            { key: "pending", label: "Needs review" },
            { key: "all", label: "All drivers" },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`kyc-tab-${t.key}`}
              className={`text-[11px] uppercase tracking-widest font-bold px-4 py-2.5 transition-colors ${
                tab === t.key ? "bg-[#FF3B30] text-white" : "text-zinc-400 hover:text-white"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="w-4 h-4 text-zinc-600 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name or email…"
            data-testid="kyc-search"
            className="w-full bg-[#141416] border border-white/[0.08] focus:border-[#FF3B30] focus:outline-none text-white placeholder-zinc-600 py-2.5 pl-9 pr-3 text-sm"
          />
        </div>
      </div>

      {loading ? (
        <div className="text-zinc-500 text-xs uppercase tracking-widest animate-pulse">Loading queue…</div>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-white/[0.08] p-12 text-center bg-[#0F0F10]">
          <div className="font-display text-2xl font-black uppercase">
            {tab === "pending" ? "Queue empty" : "No matches"}
          </div>
          <p className="text-zinc-500 text-sm mt-2">
            {tab === "pending" ? "No pending KYC submissions." : "Try a different search."}
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {filtered.map((item) => (
            <div key={item.user_id} className="border border-white/[0.08] bg-[#0F0F10] p-6" data-testid={`kyc-row-${item.user_id}`}>
              <button
                type="button"
                onClick={() => toggleExpand(item)}
                data-testid={`kyc-expand-${item.user_id}`}
                className="w-full text-left"
              >
                <div className="flex items-center justify-between flex-wrap gap-3 mb-1">
                  <div className="flex items-center gap-2">
                    {expanded === item.user_id ? (
                      <ChevronUp className="w-4 h-4 text-[#FF3B30]" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-zinc-500" />
                    )}
                    <div>
                      <div className="font-display text-xl font-black uppercase">{item.name}</div>
                      <div className="text-xs text-zinc-500">{item.email} · {item.role}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {item.verified && (
                      <span className="text-[10px] uppercase tracking-widest font-black bg-emerald-500 text-black px-2 py-1">
                        Verified
                      </span>
                    )}
                    <span
                      className={`text-[10px] uppercase tracking-widest font-black px-2 py-1 ${
                        item.pending_count > 0 ? "bg-amber-500/90 text-black" : "bg-white/[0.06] text-zinc-500"
                      }`}
                      data-testid={`pending-count-${item.user_id}`}
                    >
                      {item.pending_count} pending
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-4 flex-wrap text-[10px] uppercase tracking-widest text-zinc-500 font-bold">
                  <span className="inline-flex items-center gap-1">
                    <Star className="w-3 h-3 text-[#FF3B30]" fill="#FF3B30" />
                    {item.rating_count > 0 ? `${item.rating_avg.toFixed(1)} (${item.rating_count})` : "No ratings"}
                  </span>
                  <span>{item.completed_jobs} {item.completed_jobs === 1 ? "haul" : "hauls"}</span>
                  {item.truck && <span>{item.truck}</span>}
                  {item.member_since && (
                    <span>Since {new Date(item.member_since).toLocaleDateString()}</span>
                  )}
                  <span className="text-zinc-600 normal-case tracking-normal">
                    {expanded === item.user_id ? "Click to collapse documents" : "Click to view documents"}
                  </span>
                </div>
              </button>
              {expanded === item.user_id && (
              <>
              <div className="mt-4 text-[10px] uppercase tracking-widest text-zinc-600 font-bold" data-testid="kyc-shortcut-hint">
                Keyboard: ← → switch doc · A approve · R reject
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-4 mt-2" data-testid={`kyc-docs-${item.user_id}`}>
                {item.docs.map((d, di) => {
                  const pv = previews[d.id];
                  const focused = di === focusIdx;
                  return (
                    <div key={d.id} className={`border bg-[#0B0B0C] flex flex-col transition-all ${focused ? "border-[#FF3B30] ring-2 ring-[#FF3B30]/40" : "border-white/[0.06]"}`} data-testid={`kyc-doc-${d.id}`} data-focused={focused ? "true" : undefined}>
                      <div className="flex items-center justify-between p-3 border-b border-white/[0.06]">
                        <span className="text-[10px] uppercase tracking-widest font-bold text-[#FF3B30]">{d.doc_type}</span>
                        <span
                          className={`text-[10px] uppercase tracking-widest font-black px-2 py-0.5 ${
                            d.status === "approved"
                              ? "bg-emerald-500/90 text-black"
                              : d.status === "rejected"
                              ? "bg-red-500/90 text-white"
                              : "bg-amber-500/90 text-black"
                          }`}
                        >
                          {d.status}
                        </span>
                      </div>
                      <div className="h-64 bg-[#050505] flex items-center justify-center overflow-hidden" data-testid={`inline-preview-${d.id}`}>
                        {pv === undefined ? (
                          <span className="text-zinc-600 text-[10px] uppercase tracking-widest animate-pulse">Loading preview…</span>
                        ) : pv === "error" ? (
                          <span className="text-zinc-600 text-[10px] uppercase tracking-widest">Preview unavailable</span>
                        ) : pv.mime?.startsWith("image/") ? (
                          <img src={pv.data_url} alt={d.filename} className="max-w-full max-h-full object-contain" />
                        ) : pv.mime === "application/pdf" ? (
                          <iframe title={d.filename} src={pv.data_url} className="w-full h-full bg-white" />
                        ) : (
                          <span className="text-zinc-600 text-[10px] uppercase tracking-widest">Preview unavailable</span>
                        )}
                      </div>
                      <div className="p-3 flex flex-col gap-2 flex-1">
                        <div className="text-xs text-zinc-400 truncate">{d.filename}</div>
                        <div className="text-[10px] text-zinc-600">{(d.size_bytes / 1024).toFixed(0)} KB · {d.mime}</div>
                        {d.admin_note && (
                          <div className="text-[11px] text-red-400/90 border-l-2 border-red-500/40 pl-2" data-testid={`admin-note-${d.id}`}>
                            {d.admin_note}
                          </div>
                        )}
                        {d.status === "submitted" && (
                          <textarea
                            value={inlineNotes[d.id] || ""}
                            onChange={(e) => setInlineNotes((n) => ({ ...n, [d.id]: e.target.value }))}
                            placeholder="Reason — required to reject, optional to approve"
                            rows={1}
                            data-testid={`inline-note-${d.id}`}
                            className="w-full bg-[#141416] border border-white/[0.08] focus:border-[#FF3B30] focus:outline-none text-white placeholder-zinc-600 p-2 text-xs"
                          />
                        )}
                        <div className="mt-auto pt-1 flex items-center gap-2 flex-wrap">
                          {d.status === "submitted" && (
                            <>
                              <button
                                onClick={() => submitInline(d, true)}
                                disabled={inlineBusy === d.id}
                                data-testid={`inline-approve-${d.id}`}
                                className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold px-3 py-1.5 bg-emerald-500 text-black hover:bg-emerald-400 disabled:opacity-50 transition-colors"
                              >
                                <CheckCircle2 className="w-3 h-3" /> Approve
                              </button>
                              <button
                                onClick={() => submitInline(d, false)}
                                disabled={inlineBusy === d.id}
                                data-testid={`inline-reject-${d.id}`}
                                className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold px-3 py-1.5 bg-red-500/90 text-white hover:bg-red-500 disabled:opacity-50 transition-colors"
                              >
                                <XCircle className="w-3 h-3" /> Reject
                              </button>
                            </>
                          )}
                          <button
                            onClick={() => openReview(item, d)}
                            data-testid={`review-open-${d.id}`}
                            className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold px-3 py-1.5 border transition-all ${
                              d.status === "submitted"
                                ? "border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30] hover:text-white"
                                : "border-white/10 text-zinc-400 hover:border-white hover:text-white"
                            }`}
                          >
                            <Eye className="w-3 h-3" /> {d.status === "submitted" ? "Review" : "Preview"}
                          </button>
                          <a
                            href={rawUrl(d.id)}
                            target="_blank"
                            rel="noreferrer"
                            data-testid={`view-file-${d.id}`}
                            className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold px-3 py-1.5 border border-white/10 text-zinc-400 hover:border-white hover:text-white transition-all"
                          >
                            <ExternalLink className="w-3 h-3" /> View
                          </a>
                          <a
                            href={rawUrl(d.id, true)}
                            data-testid={`download-file-${d.id}`}
                            className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold px-3 py-1.5 border border-white/10 text-zinc-400 hover:border-[#FF3B30] hover:text-[#FF3B30] transition-all"
                          >
                            <Download className="w-3 h-3" /> Download
                          </a>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              </>
              )}
            </div>
          ))}
        </div>
      )}

      {review && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur flex items-center justify-center p-4"
          onClick={() => setReview(null)}
          data-testid="review-modal"
        >
          <div
            className="bg-[#0F0F10] border border-white/[0.08] max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-white/[0.08] sticky top-0 bg-[#0F0F10] z-10">
              <div>
                <div className="text-xs uppercase tracking-widest font-bold">{review.file.filename}</div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">
                  {review.item.name} · {review.doc.doc_type} · {(review.doc.size_bytes / 1024).toFixed(0)} KB
                </div>
                <div className="flex items-center gap-3 mt-1.5">
                  <a
                    href={rawUrl(review.doc.id)}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="review-view-original"
                    className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-zinc-400 hover:text-white transition-colors"
                  >
                    <ExternalLink className="w-3 h-3" /> Open original
                  </a>
                  <a
                    href={rawUrl(review.doc.id, true)}
                    data-testid="review-download"
                    className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-zinc-400 hover:text-[#FF3B30] transition-colors"
                  >
                    <Download className="w-3 h-3" /> Download
                  </a>
                </div>
              </div>
              <button onClick={() => setReview(null)} data-testid="review-close" className="text-zinc-500 hover:text-white" aria-label="Close">
                <XCircle className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 flex items-center justify-center bg-[#050505] min-h-[300px]" data-testid="review-preview">
              {review.file.mime?.startsWith("image/") ? (
                <img src={review.file.data_url} alt={review.file.filename} className="max-w-full max-h-[55vh] object-contain" />
              ) : review.file.mime === "application/pdf" ? (
                <iframe title={review.file.filename} src={review.file.data_url} className="w-full h-[55vh] bg-white" />
              ) : (
                <div className="text-zinc-500 text-xs uppercase">Preview unavailable</div>
              )}
            </div>

            {review.doc.status === "submitted" && (
              <div className="p-4 border-t border-white/[0.08] space-y-3">
                <div className="flex flex-wrap gap-2">
                  {REJECTION_PRESETS.map((p, i) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setReview((r) => ({ ...r, note: r.note === p ? "" : p }))}
                      data-testid={`reason-preset-${i}`}
                      className={`text-[10px] uppercase tracking-widest font-bold px-3 py-1.5 border transition-colors ${
                        review.note === p
                          ? "border-red-500 text-red-400 bg-red-500/10"
                          : "border-white/10 text-zinc-400 hover:border-red-500/50 hover:text-red-400"
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
                <textarea
                  value={review.note}
                  onChange={(e) => setReview((r) => ({ ...r, note: e.target.value }))}
                  placeholder="Rejection reason (shown to the driver) — optional for approvals"
                  rows={2}
                  data-testid="review-note"
                  className="w-full bg-[#141416] border border-white/[0.08] focus:border-[#FF3B30] focus:outline-none text-white placeholder-zinc-600 p-3 text-sm"
                />
                <div className="flex items-center gap-3 flex-wrap">
                  <button
                    onClick={() => submitReview(true)}
                    data-testid="review-approve"
                    className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-black font-bold uppercase tracking-widest text-xs px-5 py-3 transition-colors"
                  >
                    <CheckCircle2 className="w-4 h-4" /> Approve
                  </button>
                  <button
                    onClick={() => submitReview(false)}
                    data-testid="review-reject"
                    className="inline-flex items-center gap-2 bg-red-500/90 hover:bg-red-500 text-white font-bold uppercase tracking-widest text-xs px-5 py-3 transition-colors"
                  >
                    <XCircle className="w-4 h-4" /> Reject
                  </button>
                  <button
                    onClick={skipToNext}
                    data-testid="review-skip"
                    className="inline-flex items-center gap-2 text-zinc-400 hover:text-white border border-white/10 hover:border-white font-bold uppercase tracking-widest text-xs px-4 py-3 transition-colors"
                  >
                    Skip <SkipForward className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
